/**
 * UserWithdrawRequestPage Component
 * 탈퇴 요청 관리 페이지
 */
import React, { useState, useMemo } from 'react';
import { useUserWithdrawData } from '../hooks/useUserWithdrawData';
import { useDelayedLoading } from '@/shared/hooks/useDelayedLoading';
import { usePagination } from '@/shared/hooks/usePagination';
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import { WithdrawRequest } from '../types/userWithdraw.types';
import { ApproveWithdrawalRequestDto } from '../api/userWithdrawApi';
import '@/shared/styles/search-form.css';
import '@/shared/styles/common-table.css';
import '../styles/UserWithdrawRequestPage.css';

const UserWithdrawRequestPage: React.FC = () => {
    const {
        withdrawRequests,
        loading,
        contStatusCodes,
        withdrawalStatusCodes,
        fetchWithdrawRequests,
        approveWithdrawal
    } = useUserWithdrawData();

    const showLoading = useDelayedLoading(loading);

    // 페이지네이션
    const ITEMS_PER_PAGE = 15;
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: withdrawRequests, itemsPerPage: ITEMS_PER_PAGE });

    // 필터 상태
    const [filters, setFilters] = useState({
        riderName: '',
        suppName: '',
        contStatus: '',
        withdrawalStatusCd: ''
    });

    // 선택된 항목 관리
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

    // 검색 핸들러
    const handleSearch = () => {
        resetPagination();
        setSelectedItems(new Set()); // 검색 시 선택 초기화
        const searchParams: {
            riderName?: string;
            suppName?: string;
            contStatus?: string;
            withdrawalStatusCd?: string;
        } = {};
        if (filters.riderName) searchParams.riderName = filters.riderName;
        if (filters.suppName) searchParams.suppName = filters.suppName;
        if (filters.contStatus) searchParams.contStatus = filters.contStatus;
        if (filters.withdrawalStatusCd) searchParams.withdrawalStatusCd = filters.withdrawalStatusCd;
        fetchWithdrawRequests(Object.keys(searchParams).length > 0 ? searchParams : undefined);
    };

    // 전체 선택/해제
    const handleSelectAll = () => {
        // 승인되지 않은 항목만 필터링
        const selectableItems = currentItems.filter(item => {
            const isApproved = item.withdrawalStatusCd === 'APPROVED' || 
                               item.withdrawalStatusCdName === '승인';
            return !isApproved;
        });
        
        if (selectedItems.size === selectableItems.length) {
            setSelectedItems(new Set());
        } else {
            const allKeys = new Set(selectableItems.map((item) => `${item.riderId}-${item.suppId}`));
            setSelectedItems(allKeys);
        }
    };

    // 개별 선택/해제
    const handleSelectItem = (riderId: string, suppId: string) => {
        const key = `${riderId}-${suppId}`;
        const newSelected = new Set(selectedItems);
        if (newSelected.has(key)) {
            newSelected.delete(key);
        } else {
            newSelected.add(key);
        }
        setSelectedItems(newSelected);
    };

    // 선택된 항목이 현재 페이지에 모두 선택되어 있는지 확인 (승인되지 않은 항목만)
    const isAllSelected = useMemo(() => {
        if (currentItems.length === 0) return false;
        // 승인되지 않은 항목만 필터링
        const selectableItems = currentItems.filter(item => {
            const isApproved = item.withdrawalStatusCd === 'APPROVED' || 
                               item.withdrawalStatusCdName === '승인';
            return !isApproved;
        });
        if (selectableItems.length === 0) return false;
        return selectableItems.every((item) => selectedItems.has(`${item.riderId}-${item.suppId}`));
    }, [currentItems, selectedItems]);

    // 탈퇴 요청 승인 핸들러
    const handleApproveWithdrawal = async () => {
        if (selectedItems.size === 0) {
            return;
        }

        const requests: ApproveWithdrawalRequestDto[] = Array.from(selectedItems).map((key) => {
            const [riderId, suppId] = key.split('-');
            return { riderId, suppId };
        });

        const result = await approveWithdrawal(requests);
        if (result) {
            setSelectedItems(new Set());
        }
    };

    if (showLoading) {
        return <Loading />;
    }

    return (
        <div className="user-withdraw-request-page">
            <div className="page-header">
                <h1 className="page-title">탈퇴 요청 관리</h1>
            </div>

            {/* 필터 섹션 */}
            <div className="card search-card">
                <div className="card-body">
                    <div className="search-form user-withdraw-search-form">
                        {/* 라이더명 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-user" />
                                    라이더명
                                </label>
                                <input
                                    type="text"
                                    className="text-input"
                                    placeholder="라이더명 입력..."
                                    value={filters.riderName}
                                    onChange={(e) => setFilters({ ...filters, riderName: e.target.value })}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>
                        </div>

                        {/* 협력사명 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사명
                                </label>
                                <input
                                    type="text"
                                    className="text-input"
                                    placeholder="협력사명 입력..."
                                    value={filters.suppName}
                                    onChange={(e) => setFilters({ ...filters, suppName: e.target.value })}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>
                        </div>

                        {/* 계약 상태 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-file-contract" />
                                    계약 상태
                                </label>
                                <div className="status-select-wrapper">
                                    <select
                                        className="status-select"
                                        value={filters.contStatus}
                                        onChange={(e) => setFilters({ ...filters, contStatus: e.target.value })}
                                    >
                                        <option value="">전체</option>
                                        {contStatusCodes.map((code) => (
                                            <option key={code.comCode} value={code.comCode}>
                                                {code.comCdName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* 탈퇴 요청 상태 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-user-minus" />
                                    탈퇴 요청 상태
                                </label>
                                <div className="status-select-wrapper">
                                    <select
                                        className="status-select"
                                        value={filters.withdrawalStatusCd}
                                        onChange={(e) => setFilters({ ...filters, withdrawalStatusCd: e.target.value })}
                                    >
                                        <option value="">전체</option>
                                        {withdrawalStatusCodes.map((code) => (
                                            <option key={code.comCode} value={code.comCode}>
                                                {code.comCdName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* 버튼 그룹 */}
                        <div className="form-group button-group">
                            <button className="btn btn-primary" onClick={handleSearch}>
                                <i className="fas fa-search" />
                                조회
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 탈퇴 요청 목록 */}
            <div className="card">
                <div className="card-body">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <button
                                className="select-all-btn"
                                onClick={handleSelectAll}
                                disabled={withdrawRequests.length === 0}
                            >
                                {isAllSelected ? '전체 해제' : '전체 선택'}
                            </button>
                            <span style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                                선택: {selectedItems.size}개
                            </span>
                        </div>
                        <button
                            className="withdraw-btn"
                            onClick={handleApproveWithdrawal}
                            disabled={selectedItems.size === 0}
                        >
                            <i className="fas fa-check"></i>
                            <span>탈퇴</span>
                        </button>
                    </div>
                    <table className="table">
                        <thead>
                            <tr>
                                <th style={{ width: '50px' }}>
                                    {(() => {
                                        // 승인되지 않은 항목이 있는지 확인
                                        const hasSelectableItems = withdrawRequests.some(item => {
                                            const isApproved = item.withdrawalStatusCd === 'APPROVED' || 
                                                               item.withdrawalStatusCdName === '승인';
                                            return !isApproved;
                                        });
                                        return hasSelectableItems ? (
                                            <input
                                                type="checkbox"
                                                checked={isAllSelected}
                                                onChange={handleSelectAll}
                                                disabled={withdrawRequests.length === 0}
                                            />
                                        ) : null;
                                    })()}
                                </th>
                                <th style={{ width: '80px' }}>NO</th>
                                <th>라이더 ID</th>
                                <th>라이더명</th>
                                <th>협력사 ID</th>
                                <th>협력사명</th>
                                <th>계약 상태</th>
                                <th>탈퇴 요청 상태</th>
                                <th>탈퇴 요청 일시</th>
                                <th style={{ textAlign: 'right' }}>잔액</th>
                                <th style={{ textAlign: 'right' }}>미납 금액</th>
                            </tr>
                        </thead>
                        <tbody>
                            {withdrawRequests.length === 0 ? (
                                <tr>
                                    <td colSpan={11} style={{ textAlign: 'center', padding: '2rem' }}>
                                        조회된 탈퇴 요청이 없습니다.
                                    </td>
                                </tr>
                            ) : (
                                currentItems.map((request: WithdrawRequest, index: number) => {
                                    const tableIndex = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
                                    const itemKey = `${request.riderId}-${request.suppId}`;
                                    const isSelected = selectedItems.has(itemKey);
                                    // 탈퇴 요청 상태가 "승인"인 경우 체크박스 숨김
                                    const isApproved = request.withdrawalStatusCd === 'APPROVED' || 
                                                       request.withdrawalStatusCdName === '승인';
                                    return (
                                        <tr key={itemKey}>
                                            <td>
                                                {!isApproved && (
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleSelectItem(request.riderId, request.suppId)}
                                                    />
                                                )}
                                            </td>
                                            <td>{tableIndex}</td>
                                            <td>{request.riderId}</td>
                                            <td>{request.riderName || '-'}</td>
                                            <td>{request.suppId}</td>
                                            <td>{request.suppName || '-'}</td>
                                            <td>{request.contStatusName || request.contStatus}</td>
                                            <td>
                                                <span className={`badge ${
                                                    request.withdrawalStatusCd === 'REQUEST' ? 'badge-warning' : 'badge-secondary'
                                                }`}>
                                                    {request.withdrawalStatusCdName || '-'}
                                                </span>
                                            </td>
                                            <td>{request.withdrawalReqDateTime || '-'}</td>
                                            <td style={{ textAlign: 'right' }}>{request.balAmtFormatted}</td>
                                            <td style={{ textAlign: 'right' }}>{request.unpaidAmtFormatted}</td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                    {withdrawRequests.length > 0 && (
                        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={goToPage}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserWithdrawRequestPage;

